import React, { useState } from "react";
import Button from "antd/lib/button";
import Switch from "antd/lib/switch";
import TimePicker from "antd/lib/time-picker";
import Checkbox from "antd/lib/checkbox";
import isBetween from "dayjs/plugin/isBetween";
import dayjs from "dayjs";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import { message } from "antd";

dayjs.extend(isBetween);

const emptyDayCopy = {
  Tuesday: { online: false, offline: false },
  Wednesday: { online: false, offline: false },
  Thursday: { online: false, offline: false },
  Friday: { online: false, offline: false },
  Saturday: { online: false, offline: false },
  Sunday: { online: false, offline: false },
};

const emptyCopiedSlots = {
  Tuesday: { online: [], offline: [] },
  Wednesday: { online: [], offline: [] },
  Thursday: { online: [], offline: [] },
  Friday: { online: [], offline: [] },
  Saturday: { online: [], offline: [] },
  Sunday: { online: [], offline: [] },
};

const buildEmptySchedule = (days) =>
  days.reduce((acc, day) => {
    acc[day] = {
      isActive: false,
      onlineActive: false,
      offlineActive: false,
      online: [{ start: null, end: null, slotType: 1 }],
      offline: [{ start: null, end: null, slotType: 2 }],
    };
    return acc;
  }, {});

const WeeklySchedule = ({
  schedule,
  setSchedule,
  daylightSchedule,
  setDaylightSchedule,
  onDeleteSlot,
}) => {
  const [activeMode, setActiveMode] = useState("standard");
  const [copyFromStandard, setCopyFromStandard] = useState(false);
  const [showValidation] = useState(false);
  const [copyToAllDays, setCopyToAllDays] = useState({ ...emptyDayCopy });
  const [copiedSlots, setCopiedSlots] = useState({ ...emptyCopiedSlots });

  const activeSchedule = activeMode === "standard" ? schedule : daylightSchedule;
  const activeSetSchedule = activeMode === "standard" ? setSchedule : setDaylightSchedule;

  const daysOfWeek = Object.keys(activeSchedule);

  const handleModeChange = (mode) => {
    setActiveMode(mode);
    setCopyToAllDays({ ...emptyDayCopy });
    setCopiedSlots({ ...emptyCopiedSlots });
    if (mode === "standard") setCopyFromStandard(false);
  };

  const handleCopyFromStandard = (checked) => {
    setCopyFromStandard(checked);
    if (checked) {
      setDaylightSchedule(JSON.parse(JSON.stringify(schedule)));
    } else {
      setDaylightSchedule(buildEmptySchedule(Object.keys(schedule)));
    }
    setCopyToAllDays({ ...emptyDayCopy });
    setCopiedSlots({ ...emptyCopiedSlots });
  };

  const isDayValid = (day, mode) =>
    activeSchedule[day][mode].every((slot) => slot.start && slot.end);

  const isTimeSlotConflicting = (day, start, end, currentMode) => {
    if (!start || !end) return false;
    const oppositeMode = currentMode === "online" ? "offline" : "online";
    const oppositeModeActive =
      currentMode === "online"
        ? activeSchedule[day].offlineActive
        : activeSchedule[day].onlineActive;
    if (!oppositeModeActive) return false;
    const newStart = dayjs(start, "HH:mm");
    const newEnd = dayjs(end, "HH:mm");
    return activeSchedule[day][oppositeMode].some((slot) => {
      if (!slot.start || !slot.end) return false;
      const slotStart = dayjs(slot.start, "HH:mm");
      const slotEnd = dayjs(slot.end, "HH:mm");
      return (
        newStart.isBetween(slotStart, slotEnd, null, "[)") ||
        newEnd.isBetween(slotStart, slotEnd, null, "(]") ||
        (newStart.isBefore(slotStart) && newEnd.isAfter(slotEnd)) ||
        slotStart.isBetween(newStart, newEnd, null, "[)") ||
        slotEnd.isBetween(newStart, newEnd, null, "(]")
      );
    });
  };

  const handleCopyToAllDaysChange = (targetDay, mode, checked) => {
    setCopyToAllDays((prev) => ({
      ...prev,
      [targetDay]: { ...prev[targetDay], [mode]: checked },
    }));

    if (checked) {
      const mondaySlots = [...activeSchedule.Monday[mode]];
      setCopiedSlots((prev) => ({
        ...prev,
        [targetDay]: { ...prev[targetDay], [mode]: mondaySlots },
      }));
      activeSetSchedule((prev) => {
        const completeExisting = prev[targetDay][mode].filter(
          (s) => s.start && s.end
        );
        return {
          ...prev,
          [targetDay]: {
            ...prev[targetDay],
            [`${mode}Active`]: prev.Monday[`${mode}Active`],
            [mode]: [...completeExisting, ...mondaySlots],
          },
        };
      });
    } else {
      activeSetSchedule((prev) => {
        const current = prev[targetDay][mode];
        const tracked = copiedSlots[targetDay][mode];
        const remaining = current.filter(
          (s) =>
            !tracked.some(
              (c) =>
                c.start === s.start &&
                c.end === s.end &&
                c.slotType === s.slotType
            )
        );
        return {
          ...prev,
          [targetDay]: {
            ...prev[targetDay],
            [mode]:
              remaining.length > 0
                ? remaining
                : [{ start: null, end: null, slotType: mode === "online" ? 1 : 2 }],
          },
        };
      });
      setCopiedSlots((prev) => ({
        ...prev,
        [targetDay]: { ...prev[targetDay], [mode]: [] },
      }));
    }
  };

  const handleToggle = (day, mode) => {
    const currentModeActive =
      mode === "online"
        ? activeSchedule[day].onlineActive
        : activeSchedule[day].offlineActive;

    activeSetSchedule((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [mode === "online" ? "onlineActive" : "offlineActive"]: !currentModeActive,
        [mode]:
          !currentModeActive && prev[day][mode].length === 0
            ? [{ start: null, end: null, slotType: mode === "online" ? 1 : 2 }]
            : currentModeActive
            ? [{ start: null, end: null, slotType: mode === "online" ? 1 : 2 }]
            : prev[day][mode],
      },
    }));

    if (currentModeActive && day !== "Monday") {
      setCopyToAllDays((prev) => ({
        ...prev,
        [day]: { ...prev[day], [mode]: false },
      }));
      setCopiedSlots((prev) => ({
        ...prev,
        [day]: { ...prev[day], [mode]: [] },
      }));
    }
  };

  const handleTimeChange = (day, index, time, type, slotType) => {
    activeSetSchedule((prev) => {
      const mode = slotType === 1 ? "online" : "offline";
      const updatedSlots = [...prev[day][mode]];
      const currentSlot = updatedSlots[index];

      if (!time) {
        updatedSlots[index] = { ...updatedSlots[index], [type]: null };
        return { ...prev, [day]: { ...prev[day], [mode]: updatedSlots } };
      }

      const newTime = time.format("HH:mm");
      const existingStart = type === "start" ? newTime : currentSlot.start;
      const existingEnd = type === "end" ? newTime : currentSlot.end;

      if (existingStart && existingEnd) {
        const startMoment = dayjs(existingStart, "HH:mm");
        const endMoment = dayjs(existingEnd, "HH:mm");
        if (startMoment.isAfter(endMoment)) {
          message.error("Start time must be before end time.");
          return prev;
        }
        if (startMoment.isSame(endMoment)) {
          message.error("Start and end times cannot be the same.");
          return prev;
        }
        if (isTimeSlotConflicting(day, existingStart, existingEnd, mode)) {
          message.error(
            `This time slot conflicts with existing ${
              mode === "online" ? "offline" : "online"
            } slots.`
          );
          return prev;
        }
      }

      const isOverlapping = updatedSlots.some((slot, i) => {
        if (i === index) return false;
        const slotStart = slot.start ? dayjs(slot.start, "HH:mm") : null;
        const slotEnd = slot.end ? dayjs(slot.end, "HH:mm") : null;
        const newStart = existingStart ? dayjs(existingStart, "HH:mm") : null;
        const newEnd = existingEnd ? dayjs(existingEnd, "HH:mm") : null;
        return (
          slotStart &&
          slotEnd &&
          newStart &&
          newEnd &&
          (newStart.isBetween(slotStart, slotEnd, null, "[)") ||
            newEnd.isBetween(slotStart, slotEnd, null, "(]") ||
            (newStart.isBefore(slotStart) && newEnd.isAfter(slotEnd)))
        );
      });

      if (isOverlapping) {
        message.error("Time slots should not overlap.");
        return prev;
      }

      if (currentSlot.id && currentSlot.isPatched) {
        onDeleteSlot && onDeleteSlot(currentSlot.id);
        updatedSlots[index] = {
          start: existingStart,
          end: existingEnd,
          slotType: currentSlot.slotType,
        };
      } else {
        updatedSlots[index] = { ...updatedSlots[index], [type]: newTime };
      }

      return { ...prev, [day]: { ...prev[day], [mode]: updatedSlots } };
    });
  };

  const addTimeSlot = (day, slotType) => {
    const mode = slotType === 1 ? "online" : "offline";
    if (!isDayValid(day, mode)) {
      message.error("Please complete the existing slot before adding a new one.");
      return;
    }
    activeSetSchedule((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [mode]: [...prev[day][mode], { start: null, end: null, slotType }],
      },
    }));
  };

  const removeTimeSlot = (day, index, slotType) => {
    const mode = slotType === 1 ? "online" : "offline";
    const removedSlot = activeSchedule[day][mode][index];
    if (removedSlot?.id) {
      onDeleteSlot && onDeleteSlot(removedSlot.id);
    }
    activeSetSchedule((prev) => {
      const currentSlots = prev[day][mode];
      if (currentSlots.length === 1) {
        return {
          ...prev,
          [day]: {
            ...prev[day],
            [slotType === 1 ? "onlineActive" : "offlineActive"]: false,
            [mode]: [{ start: null, end: null, slotType }],
          },
        };
      }
      return {
        ...prev,
        [day]: { ...prev[day], [mode]: currentSlots.filter((_, i) => i !== index) },
      };
    });
  };

  const renderTimeSlotWithConflictCheck = (day, slot, index, mode) => {
    const slotType = mode === "online" ? 1 : 2;
    const isConflicting =
      slot.start && slot.end && isTimeSlotConflicting(day, slot.start, slot.end, mode);
    return (
      <div key={index} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <TimePicker
          value={slot.start ? dayjs(slot.start, "HH:mm") : null}
          placeholder="Start"
          format="HH:mm"
          size="small"
          onChange={(time) => handleTimeChange(day, index, time, "start", slotType)}
          status={
            showValidation && !slot.start
              ? "error"
              : isConflicting
              ? "warning"
              : undefined
          }
        />
        <TimePicker
          value={slot.end ? dayjs(slot.end, "HH:mm") : null}
          placeholder="End"
          format="HH:mm"
          size="small"
          onChange={(time) => handleTimeChange(day, index, time, "end", slotType)}
          status={
            showValidation && !slot.end
              ? "error"
              : isConflicting
              ? "warning"
              : undefined
          }
        />
        <Button
          icon={<DeleteOutlined />}
          onClick={() => removeTimeSlot(day, index, slotType)}
          size="small"
          style={{
            backgroundColor: "#ff4d4f",
            borderRadius: "10%",
            width: "24px",
            height: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            color: "white",
          }}
        />
        {isConflicting && (
          <span style={{ fontSize: "10px", color: "#faad14" }}>⚠️ Conflicts</span>
        )}
      </div>
    );
  };

  return (
    <div className="instformWeeklySchedule">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "10px",
          marginBottom: "10px",
        }}
      >
        <h2 className="text-[20px] font-semibold mb-2" style={{ fontSize: "20px", margin: 0 }}>
          Weekly Schedule
        </h2>
        <div style={{ display: "flex", border: "1px solid #d9d9d9", borderRadius: "6px", overflow: "hidden" }}>
          {["standard", "daylight"].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => handleModeChange(tab)}
              style={{
                padding: "4px 16px",
                fontSize: "13px",
                fontWeight: activeMode === tab ? 600 : 400,
                background: activeMode === tab ? "#000" : "#fff",
                color: activeMode === tab ? "#fff" : "#333",
                border: "none",
                cursor: "pointer",
                textTransform: "capitalize",
              }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="instform_content border p-5 rounded-xl">
        <div className="instform_content_data">
          {activeMode === "daylight" && (
            <div
              style={{
                marginBottom: "14px",
                padding: "8px 12px",
                background: "#f9f9f9",
                borderRadius: "6px",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Checkbox
                checked={copyFromStandard}
                onChange={(e) => handleCopyFromStandard(e.target.checked)}
              >
                <span style={{ fontSize: "13px", color: "#333" }}>
                  Copy all slots from Standard
                </span>
              </Checkbox>
            </div>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              marginBottom: "15px",
              paddingBottom: "10px",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <h4 style={{ margin: 0, width: "80px", minWidth: "80px", fontWeight: "600" }}>Day</h4>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: "14px", color: "#3fd97f", fontWeight: "600" }}>
                Online Class Slots
              </span>
            </div>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: "14px", color: "#ff9c55", fontWeight: "600" }}>
                Offline Class Slots
              </span>
            </div>
          </div>

          {daysOfWeek.map((day, dayIndex) => (
            <div key={day}>
              <div style={{ marginBottom: "20px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                  <h4 style={{ margin: 0, width: "80px", minWidth: "80px" }}>{day}</h4>

                  {/* Online */}
                  <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    <Switch
                      checked={activeSchedule[day].onlineActive}
                      onChange={() => handleToggle(day, "online")}
                    />
                    <span style={{ fontSize: "12px", color: "#3fd97f", minWidth: "45px" }}>Online</span>
                    {day !== "Monday" && (
                      <Checkbox
                        checked={copyToAllDays[day]?.online || false}
                        onChange={(e) => handleCopyToAllDaysChange(day, "online", e.target.checked)}
                        style={{ marginRight: "5px" }}
                      >
                        <span style={{ fontSize: "11px", color: "#3fd97f" }}>Copy</span>
                      </Checkbox>
                    )}
                    {activeSchedule[day].onlineActive ? (
                      <>
                        {activeSchedule[day].online.map((slot, index) =>
                          renderTimeSlotWithConflictCheck(day, slot, index, "online")
                        )}
                        {isDayValid(day, "online") && (
                          <Button
                            type="dashed"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => addTimeSlot(day, 1)}
                          />
                        )}
                      </>
                    ) : (
                      <span style={{ color: "#999", fontSize: "12px" }}>Unavailable</span>
                    )}
                  </div>

                  {/* Offline */}
                  <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    <Switch
                      checked={activeSchedule[day].offlineActive}
                      onChange={() => handleToggle(day, "offline")}
                    />
                    <span style={{ fontSize: "12px", color: "#ff9c55", minWidth: "45px" }}>Offline</span>
                    {day !== "Monday" && (
                      <Checkbox
                        checked={copyToAllDays[day]?.offline || false}
                        onChange={(e) => handleCopyToAllDaysChange(day, "offline", e.target.checked)}
                        style={{ marginRight: "5px" }}
                      >
                        <span style={{ fontSize: "11px", color: "#ff9c55" }}>Copy</span>
                      </Checkbox>
                    )}
                    {activeSchedule[day].offlineActive ? (
                      <>
                        {activeSchedule[day].offline.map((slot, index) =>
                          renderTimeSlotWithConflictCheck(day, slot, index, "offline")
                        )}
                        {isDayValid(day, "offline") && (
                          <Button
                            type="dashed"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => addTimeSlot(day, 2)}
                          />
                        )}
                      </>
                    ) : (
                      <span style={{ color: "#999", fontSize: "12px" }}>Unavailable</span>
                    )}
                  </div>
                </div>
              </div>

              {dayIndex < daysOfWeek.length - 1 && (
                <div style={{ borderBottom: "1px solid #e8e8e8", marginBottom: "20px", width: "100%" }} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeeklySchedule;
